import { ApolloQueryResult, FetchPolicy, FetchResult } from '@apollo/client';
import {
  CartBrowsingCriteria,
  CartItem,
  Portfolio,
  Product,
  RateSegment,
} from '@insurancemenu/imenu-components-lib/dist/types/productCardsTypings';
import { ProductLineId } from '@insurancemenu/imenu-components-lib/dist/types/ProductLineId';
import apolloClient from 'api/apolloClient';
import {
  GET_FULL_PRODUCT_TEMPLATE_BY_ID,
  CREATE_DYNAMIC_PORTFOLIO,
  CREATE_DYNAMIC_PORTFOLIO_RFQ,
  ADD_PRODUCT_TAG,
  REMOVE_PRODUCT_TAG,
  GET_CATALOG,
  CREATE_PRODUCT_TEMPLATE,
  DUPLICATE_PRODUCT_TEMPLATE,
  GENERATE_TEMPLATE_JSON,
  GET_PRODUCT_TEMPLATES_BY_SPONSOR_ID,
  GET_PRODUCT_TEMPLATE_CHECKLIST,
  GET_SHORT_PRODUCT_TEMPLATE_BY_ID,
  UPDATE_PRODUCT_TEMPLATE,
  CHANGE_ARCHIVE_STATUS,
  SAVE_ANSWERS_AND_COMPLETE_STEP,
  COMPLETE_CHECKLIST_STEP,
  MARK_PRODUCT_TEMPLATE_AS_STOCK,
  SAVE_ANSWERS,
  ADD_COLLATERAL,
  ADD_CUSTOM_RATES,
  COPY_RFQ_PORTFOLIO_DATA,
  GET_PORTFOLIOS,
  GET_PORTFOLIOS_BY_ID,
  REMOVE_COLLATERAL,
  REMOVE_CUSTOM_RATES,
  WIPE_RFQ_PORTFOLIO_DATA,
  PROCESS_RFQ_TEMPLATE_FILE,
  DELETE_TEMPLATE,
  GET_AVAILABLE_CARRIERS,
  GET_AVAILABLE_PRODUCTS,
  GET_PRODUCT_BY_ID,
  GET_SHELF_PRODUCT_TEMPLATES_BY_SPONSOR_ID,
  GET_INITIAL_SEED_PRODUCTS_BY_SPONSOR_ID,
  REMOVE_INITIAL_SEED,
  GET_JSONS_UPLOAD_URLS,
  GET_INITIAL_SEED_JSONS_ZIP,
  REMOVE_CUSTOM_ANSWER,
  SAVE_REQUIRED_ANSWERS,
  SAVE_CUSTOM_ANSWERS,
  UNCOMPLETE_CHECKLIST_STEP,
  OVERRIDE_RATES,
  GET_BENEFIT_SUMMARY_UPLOAD_URL,
  REMOVE_BENEFIT_SUMMARY,
  GET_PORTFOLIO_BY_ID,
  CREATE_RENEWAL_RFQ_PORTFOLIOS,
} from 'api/queries/productServiceV2';
import { PaginationDTO } from 'types/general';
import {
  CartItemPortfolioCreationPresenter,
  Catalog,
  CreateProductTemplateInput,
  InitialSeedInput,
  InitialSeedProductTemplate,
  InputFormAnswers,
  PortfolioRequest,
  ProductTemplate,
  ProductTemplateChecklistCard,
  ProductTemplateInput,
  ProposalProductTagData,
  ProposalProductTagDataWithTag,
  RFQCarrierAvailability,
  ShelfProductInfo,
  ShelfProductTemplateInput,
  UpdateProductTemplateInput,
  RatesOverride,
} from 'types/productServiceV2';

interface DuplicateProductTemplateInput {
  id: string;
  externalName: string;
  internalName: string;
  groupId?: string | null;
}

// Catalog

export async function getCatalog(
  domainId: string,
  effectiveDate: string,
  rateSegment: RateSegment,
  state?: string,
): Promise<ApolloQueryResult<{ getCatalog: Catalog }>> {
  return apolloClient.query<{ getCatalog: Catalog }>({
    query: GET_CATALOG,
    variables: { domainId, effectiveDate, rateSegment, state },
  });
}

export async function getCatalogWithNoCache(
  domainId: string,
  effectiveDate: string,
  rateSegment: RateSegment,
  state?: string,
): Promise<ApolloQueryResult<{ getCatalog: Catalog }>> {
  return apolloClient.query<{ getCatalog: Catalog }>({
    query: GET_CATALOG,
    variables: { domainId, effectiveDate, rateSegment, state },
    fetchPolicy: 'no-cache',
  });
}

// Portfolio

export async function getPortfolios(
  request: PortfolioRequest,
): Promise<ApolloQueryResult<{ getPortfolios: Portfolio[] }>> {
  return apolloClient.query<{ getPortfolios: Portfolio[] }>({
    query: GET_PORTFOLIOS,
    variables: { request },
    fetchPolicy: 'no-cache',
  });
}

export async function getPortfoliosById(
  cartItems: CartItem[],
  lastUpdateBrowsingCriteria?: CartBrowsingCriteria,
  groupId?: string,
  cartId?: string,
  fetchPolicy: FetchPolicy = 'cache-first',
): Promise<Portfolio[]> {
  const convertedInput = cartItems.map((cartItem) => {
    return {
      portfolioId: cartItem.portfolioId,
      browsingCriteria: cartItem.browsingCriteria,
    };
  });
  return (
    await apolloClient.query<{ getPortfoliosByIdWithBC: Portfolio[] }>({
      query: GET_PORTFOLIOS_BY_ID,
      fetchPolicy,
      variables: { cartItems: convertedInput, lastUpdateBC: lastUpdateBrowsingCriteria, groupId, cartId },
    })
  ).data.getPortfoliosByIdWithBC;
}

export async function getPortfolioById(portfolioId: string, fetchProducts: boolean = false): Promise<Portfolio> {
  return (
    await apolloClient.query<{ getPortfolioById: Portfolio }>({
      query: GET_PORTFOLIO_BY_ID,
      fetchPolicy: 'no-cache',
      variables: { portfolioId, fetchProducts },
    })
  ).data.getPortfolioById;
}

export async function createDynamicPortfolio(
  portfolioIds: string[],
  name: string,
): Promise<FetchResult<{ createDynamicPortfolio: { id: string } }>> {
  return apolloClient.mutate<{ createDynamicPortfolio: { id: string } }>({
    mutation: CREATE_DYNAMIC_PORTFOLIO,
    fetchPolicy: 'no-cache',
    variables: { portfolioIds, name },
  });
}

export async function createDynamicPortfolioRFQ(
  portfolioIds: string[],
  name: string,
  cartId: string,
  productLineId: ProductLineId,
  cartItems: CartItemPortfolioCreationPresenter[],
): Promise<FetchResult<{ createDynamicRFQPortfolio: { id: string } }>> {
  return apolloClient.mutate<{ createDynamicRFQPortfolio: { id: string } }>({
    mutation: CREATE_DYNAMIC_PORTFOLIO_RFQ,
    fetchPolicy: 'no-cache',
    variables: { portfolioIds, name, cartId, productLineId, cartItems },
  });
}

// Tags

export async function manageProductTag({
  portfolioId,
  productId,
  periodId,
  tag,
}: ProposalProductTagDataWithTag): Promise<FetchResult<{ addProductTag: boolean }>> {
  return apolloClient.mutate<{ addProductTag: boolean }>({
    mutation: ADD_PRODUCT_TAG,
    fetchPolicy: 'no-cache',
    variables: { portfolioId, productId, tag, periodId },
  });
}

export async function removeProductTag({
  portfolioId,
  productId,
  periodId,
}: ProposalProductTagData): Promise<FetchResult<{ removeProductTag: boolean }>> {
  return apolloClient.mutate<{ removeProductTag: boolean }>({
    mutation: REMOVE_PRODUCT_TAG,
    fetchPolicy: 'no-cache',
    variables: { portfolioId, productId, periodId },
  });
}

// Templates

export async function getFullProductTemplateInfo(id: string): Promise<ProductTemplate> {
  const res = await apolloClient.query<{ getProductTemplateById: ProductTemplate }>({
    query: GET_FULL_PRODUCT_TEMPLATE_BY_ID,
    fetchPolicy: 'no-cache',
    variables: { id },
  });
  return res.data.getProductTemplateById;
}

export async function getShortProductTemplateInfo(id: string): Promise<ProductTemplate> {
  const res = await apolloClient.query<{ getProductTemplateById: ProductTemplate }>({
    query: GET_SHORT_PRODUCT_TEMPLATE_BY_ID,
    fetchPolicy: 'no-cache',
    variables: { id },
  });
  return res.data.getProductTemplateById;
}

export async function getProductTemplateChecklist(id: string): Promise<ProductTemplate> {
  const res = await apolloClient.query<{ getProductTemplateById: ProductTemplate }>({
    query: GET_PRODUCT_TEMPLATE_CHECKLIST,
    fetchPolicy: 'no-cache',
    variables: { id },
  });
  return res.data.getProductTemplateById;
}

export async function generateTemplateJson(templateId: string): Promise<string | undefined> {
  return (
    await apolloClient.mutate<{ generateTemplateJson: string }>({
      mutation: GENERATE_TEMPLATE_JSON,
      fetchPolicy: 'no-cache',
      variables: { id: templateId },
    })
  ).data?.generateTemplateJson;
}

export async function getAvailableCarriers(
  state: string,
  effectiveDate: string,
  rateSegment: RateSegment = 'CUSTOM',
): Promise<RFQCarrierAvailability[]> {
  const res = await apolloClient.query<{ getAvailableCarriers: RFQCarrierAvailability[] }>({
    query: GET_AVAILABLE_CARRIERS,
    fetchPolicy: 'no-cache',
    variables: { state, effectiveDate, rateSegment },
  });
  return res.data.getAvailableCarriers;
}

export async function getAvailableProducts(
  sponsorId: string,
  effectiveDate: string,
  rateSegment: RateSegment,
  productLineName: string,
  state?: string,
): Promise<Product[]> {
  const res = await apolloClient.query<{ getAvailableProducts: Product[] }>({
    query: GET_AVAILABLE_PRODUCTS,
    fetchPolicy: 'no-cache',
    variables: { sponsorId, effectiveDate, rateSegment, productLineName, state },
  });
  return res.data.getAvailableProducts;
}

export async function getProductById(productId: string): Promise<Product> {
  const res = await apolloClient.query<{ getProductById: Product }>({
    query: GET_PRODUCT_BY_ID,
    fetchPolicy: 'no-cache',
    variables: { productId },
  });
  return res.data.getProductById;
}

export async function getProductTemplates(
  productTemplateInput: ProductTemplateInput,
): Promise<PaginationDTO<ProductTemplate>> {
  const res = await apolloClient.query<{ getProductTemplatesBySponsorId: PaginationDTO<ProductTemplate> }>({
    query: GET_PRODUCT_TEMPLATES_BY_SPONSOR_ID,
    fetchPolicy: 'no-cache',
    variables: { ...productTemplateInput },
  });
  return res.data.getProductTemplatesBySponsorId;
}

export function createProductTemplate(
  template: CreateProductTemplateInput,
): Promise<FetchResult<{ createProductTemplate: ProductTemplate }>> {
  return apolloClient.mutate<{ createProductTemplate: ProductTemplate }>({
    mutation: CREATE_PRODUCT_TEMPLATE,
    variables: { template },
  });
}

export function updateProductTemplate(
  productTemplate: UpdateProductTemplateInput,
): Promise<FetchResult<{ updateProductTemplate: ProductTemplate }>> {
  return apolloClient.mutate<{ updateProductTemplate: ProductTemplate }>({
    mutation: UPDATE_PRODUCT_TEMPLATE,
    variables: { productTemplate },
  });
}

export function duplicateProductTemplate({
  id,
  externalName,
  internalName,
  groupId,
}: DuplicateProductTemplateInput): Promise<FetchResult<{ duplicateProductTemplate: ProductTemplate }>> {
  return apolloClient.mutate<{ duplicateProductTemplate: ProductTemplate }>({
    mutation: DUPLICATE_PRODUCT_TEMPLATE,
    variables: { id, name: externalName, internalName, groupId },
  });
}

export function saveAnswerAndCompleteStep(
  id: string,
  answers: InputFormAnswers,
  card: ProductTemplateChecklistCard,
): Promise<FetchResult<{ saveAnswersAndCompleteStep: ProductTemplate }>> {
  return apolloClient.mutate<{ saveAnswersAndCompleteStep: ProductTemplate }>({
    mutation: SAVE_ANSWERS_AND_COMPLETE_STEP,
    variables: { id, answers, card },
  });
}

export function changeArchiveStatus(
  id: string,
  archive: boolean,
): Promise<FetchResult<{ changeArchiveStatus: ProductTemplate }>> {
  return apolloClient.mutate<{ changeArchiveStatus: ProductTemplate }>({
    mutation: CHANGE_ARCHIVE_STATUS,
    variables: { id, archive },
  });
}

export function completeChecklistStep(
  id: string,
  card: ProductTemplateChecklistCard,
): Promise<FetchResult<{ completeChecklistStep: ProductTemplate }>> {
  return apolloClient.mutate<{ completeChecklistStep: ProductTemplate }>({
    mutation: COMPLETE_CHECKLIST_STEP,
    variables: { id, card },
  });
}

export async function uncompleteChecklistStep(
  id: string,
  card: ProductTemplateChecklistCard,
): Promise<ProductTemplate> {
  const res = await apolloClient.mutate<{ uncompleteChecklistStep: ProductTemplate }>({
    mutation: UNCOMPLETE_CHECKLIST_STEP,
    variables: { id, card },
  });
  return res.data!.uncompleteChecklistStep;
}

export function markProductTemplateAsStock(
  id: string,
): Promise<FetchResult<{ markProductTemplateAsStock: ProductTemplate }>> {
  return apolloClient.mutate<{ markProductTemplateAsStock: ProductTemplate }>({
    mutation: MARK_PRODUCT_TEMPLATE_AS_STOCK,
    variables: { id },
  });
}

export function saveAnswers(
  id: string,
  answers: InputFormAnswers,
): Promise<FetchResult<{ saveAnswers: ProductTemplate }>> {
  return apolloClient.mutate<{ saveAnswers: ProductTemplate }>({
    mutation: SAVE_ANSWERS,
    variables: { id, answers },
  });
}

// RFQ quoting

export async function addCustomRates(
  cartId: string,
  portfolioId: string,
  bcIndex: number,
  formAnswers: Record<string, string>,
): Promise<FetchResult<{ addCustomRates: boolean }>> {
  return apolloClient.mutate<{ addCustomRates: boolean }>({
    mutation: ADD_CUSTOM_RATES,
    fetchPolicy: 'no-cache',
    variables: { cartId, portfolioId, bcIndex, formAnswers },
  });
}

export async function addCollateral(
  cartId: string,
  portfolioId: string,
  productId: string,
  fileName: string,
  linkName: string,
): Promise<FetchResult<{ addCollateral: string }>> {
  return apolloClient.mutate<{ addCollateral: string }>({
    mutation: ADD_COLLATERAL,
    fetchPolicy: 'no-cache',
    variables: { cartId, portfolioId, productId, fileName, linkName },
  });
}

export async function removeCollateral(
  cartId: string,
  portfolioId: string,
  productId: string,
  linkName: string,
): Promise<FetchResult<{ removeCollateral: boolean }>> {
  return apolloClient.mutate<{ removeCollateral: boolean }>({
    mutation: REMOVE_COLLATERAL,
    fetchPolicy: 'no-cache',
    variables: { cartId, portfolioId, productId, linkName },
  });
}

export async function wipeRFQPortfolioData(
  cartId: string,
  portfolioId: string,
): Promise<FetchResult<{ wipeRFQPortfolioData: boolean }>> {
  return apolloClient.mutate<{ wipeRFQPortfolioData: boolean }>({
    mutation: WIPE_RFQ_PORTFOLIO_DATA,
    fetchPolicy: 'no-cache',
    variables: { cartId, portfolioId },
  });
}

export async function removeCustomRates(
  cartId: string,
  portfolioId: string,
  bcIndex: number,
): Promise<FetchResult<{ removeCustomRates: boolean }>> {
  return apolloClient.mutate<{ removeCustomRates: boolean }>({
    mutation: REMOVE_CUSTOM_RATES,
    fetchPolicy: 'no-cache',
    variables: { cartId, portfolioId, bcIndex },
  });
}

export async function copyRFQPortfolioData(
  cartId: string,
  newCartId: string,
): Promise<FetchResult<{ copyRFQPortfolioData: boolean }>> {
  return apolloClient.mutate<{ copyRFQPortfolioData: boolean }>({
    mutation: COPY_RFQ_PORTFOLIO_DATA,
    fetchPolicy: 'no-cache',
    variables: { cartId, newCartId },
  });
}

export async function processRfqTemplateFile(
  productId: string,
  name: string,
  sponsorId: string,
): Promise<FetchResult<{ processRfqTemplateFile: ProductTemplate }>> {
  return apolloClient.mutate<{ processRfqTemplateFile: ProductTemplate }>({
    mutation: PROCESS_RFQ_TEMPLATE_FILE,
    fetchPolicy: 'no-cache',
    variables: { productId, name, sponsorId },
  });
}

export async function deleteTemplate(id: string): Promise<ProductTemplate> {
  const res = await apolloClient.mutate<{ deleteTemplate: ProductTemplate }>({
    mutation: DELETE_TEMPLATE,
    fetchPolicy: 'no-cache',
    variables: { id },
  });

  return res.data!.deleteTemplate;
}

export async function getShelfProductTemplates(
  shelfProductTemplateInput: ShelfProductTemplateInput,
): Promise<PaginationDTO<ShelfProductInfo>> {
  const res = await apolloClient.query<{ getShelfProductTemplates: PaginationDTO<ShelfProductInfo> }>({
    query: GET_SHELF_PRODUCT_TEMPLATES_BY_SPONSOR_ID,
    fetchPolicy: 'no-cache',
    variables: { ...shelfProductTemplateInput },
  });
  return res.data.getShelfProductTemplates;
}

export async function getInitialSeedProductTemplatesBySponsorId(
  initialSeedInput: InitialSeedInput,
): Promise<PaginationDTO<InitialSeedProductTemplate>> {
  const res = await apolloClient.query<{
    getInitialSeedProductTemplatesBySponsorId: PaginationDTO<InitialSeedProductTemplate>;
  }>({
    query: GET_INITIAL_SEED_PRODUCTS_BY_SPONSOR_ID,
    fetchPolicy: 'no-cache',
    variables: { ...initialSeedInput },
  });
  return res.data.getInitialSeedProductTemplatesBySponsorId;
}

export async function removeInitialSeed(id: string): Promise<FetchResult<{ removeInitialSeed: boolean }>> {
  return apolloClient.mutate<{ removeInitialSeed: boolean }>({
    mutation: REMOVE_INITIAL_SEED,
    fetchPolicy: 'no-cache',
    variables: { id },
  });
}

export async function getInitialSeedUploadUrls(
  sponsorId: string,
  fileNames: string[],
): Promise<FetchResult<Record<string, string>>> {
  return (
    await apolloClient.query<{ getInitialSeedUploadUrls: Record<string, string> }>({
      query: GET_JSONS_UPLOAD_URLS,
      fetchPolicy: 'no-cache',
      variables: { sponsorId, fileNames },
    })
  ).data.getInitialSeedUploadUrls;
}

export async function getBenefitSummaryUploadUrl(templateId: string, fileName: string): Promise<string> {
  return (
    await apolloClient.query<{ getBenefitSummaryUploadUrl: string }>({
      query: GET_BENEFIT_SUMMARY_UPLOAD_URL,
      fetchPolicy: 'no-cache',
      variables: { templateId, fileName },
    })
  ).data.getBenefitSummaryUploadUrl;
}

export async function getInitialSeedJSONsZip(sponsorId: string): Promise<string> {
  return (
    await apolloClient.mutate<{ getInitialSeedJsonsZip: string }>({
      mutation: GET_INITIAL_SEED_JSONS_ZIP,
      fetchPolicy: 'no-cache',
      variables: { sponsorId },
    })
  ).data!.getInitialSeedJsonsZip;
}

export async function removeCustomAnswer(
  templateId: string,
  attributeId: string,
): Promise<FetchResult<{ removeCustomAnswer: boolean }>> {
  return apolloClient.mutate<{ removeCustomAnswer: boolean }>({
    mutation: REMOVE_CUSTOM_ANSWER,
    fetchPolicy: 'no-cache',
    variables: { id: templateId, attributeId },
  });
}

export async function saveRequiredAnswers(
  templateId: string,
  attributeId: string,
  answers: Record<string, string>,
): Promise<ProductTemplate> {
  const res = await apolloClient.mutate<{ saveRequiredAnswers: ProductTemplate }>({
    mutation: SAVE_REQUIRED_ANSWERS,
    fetchPolicy: 'no-cache',
    variables: { id: templateId, attributeId, answers },
  });
  return res.data!.saveRequiredAnswers;
}

export async function saveCustomAnswers(templateId: string, answers: Record<string, string>): Promise<ProductTemplate> {
  const res = await apolloClient.mutate<{ saveCustomAnswers: ProductTemplate }>({
    mutation: SAVE_CUSTOM_ANSWERS,
    fetchPolicy: 'no-cache',
    variables: { id: templateId, answers },
  });
  return res.data!.saveCustomAnswers;
}

export async function overrideRates(
  cartId: string,
  ratesOverride: RatesOverride[],
): Promise<FetchResult<{ overrideRates: boolean }>> {
  return apolloClient.mutate<{ overrideRates: boolean }>({
    mutation: OVERRIDE_RATES,
    fetchPolicy: 'no-cache',
    variables: { cartId, ratesOverride },
  });
}

export async function removeBenefitSummary(
  templateId: string,
): Promise<FetchResult<{ removeBenefitSummary: boolean }>> {
  return apolloClient.mutate<{ removeBenefitSummary: boolean }>({
    mutation: REMOVE_BENEFIT_SUMMARY,
    fetchPolicy: 'no-cache',
    variables: { templateId },
  });
}

export async function createRenewalRFQPortfolios(groupId: string, initialSeedIds: string[]): Promise<boolean> {
  return (
    await apolloClient.mutate<{ createRenewalRFQPortfolios: boolean }>({
      mutation: CREATE_RENEWAL_RFQ_PORTFOLIOS,
      fetchPolicy: 'no-cache',
      variables: { groupId, initialSeedIds },
    })
  ).data!.createRenewalRFQPortfolios;
}
